import { browserStandard, browserStandardTab } from '../../shared/buehnen-browser-standard';
import { z } from 'zod';
import { GuideHttpFehler, guideNetzFehler, mitNetzWiederholung } from '../lib/guideNetz';
import SchnellbuehnePanel from './panels/SchnellbuehnePanel';
import './SchnellbuehnenFenster.css';
import { useCallback, useEffect, useRef, useState, type ReactNode, type CSSProperties, type MouseEvent, type SyntheticEvent } from 'react';
import { Actions, DockLocation, Layout, Model, type IJsonModel, type ITabRenderValues, type TabNode } from 'flexlayout-react';
import { BuehnenKennungKontext, BuehnenKontext, istEigeneCuiSeite, OEFFNEN_EREIGNIS } from '../lib/buehnenAdresszeile';
import { useAuth } from '../contexts/AuthContext';
import { buehnenAnsichtMelden, useKopfBietetBuehnenWahl } from '../lib/buehnenAnsicht';
import { alleTabs, basisTabs, dokumentTab, istFesteAnsicht, standardAnsicht, wiederherstellen,
  type Anordnung, type BuehnenBezug, type BuehnenModus, type BuehnenProfil, type GespeicherteAnordnung } from '../../shared/buehnen-anordnung';

const anordnungenAntwort = z.object({ anordnungen: z.record(z.object({
  version: z.number().int().nonnegative(), am: z.string(), hinweis: z.string(),
  anordnung: z.object({
    basis: z.array(z.object({ id: z.string(), key: z.string(), name: z.string() })),
    layout: z.custom<IJsonModel>(v => {
      if (!v || typeof v !== 'object' || !('layout' in v)) return false;
      try { Model.fromJson(v as IJsonModel); return true; } catch { return false; }
    }),
  }),
})) });

const controlStyle: CSSProperties = { minHeight: 24, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--tn-border)', background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', fontSize: 12, cursor: 'pointer' };
// Serialize saves across mode switches/remounts, not only inside one component.
const speicherQueues = new Map<string, Promise<void>>();
const modi: Array<[BuehnenModus, string]> = [['dokument', 'Dokument'], ['tabs', 'Tabs'], ['buehne', 'Bühne']];
/** Unter dieser Breite der Buehnenflaeche gilt eine eigene ausdrueckliche Wahl (eigener Schluessel, s. u.). */
const SCHMAL_PX = 900;
/** Ohne ausdrueckliche Wahl: Touch = Reiter, Desktop = nebeneinander — unabhaengig von der Breite. */
export function vorgabeModus(profil: BuehnenProfil): BuehnenModus { return profil === 'touch' ? 'tabs' : 'buehne'; }
function lesen<T>(key: string): T | null { try { return JSON.parse(localStorage.getItem(key) ?? 'null'); } catch { return null; } }
function merken(key: string, value: unknown) { localStorage.setItem(key, JSON.stringify(value)); }
function fingerprint(doc: unknown): string {
  const text = JSON.stringify(doc); let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = (hash * 33 + text.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}
interface Props {
  doc: IJsonModel; bezug?: BuehnenBezug; factory: (node: TabNode) => ReactNode;
  onModel: (model: Model | null) => void;
}
interface AeussereProps extends Props {
  /** false = angeheftet und versteckt montiert (lib/buehnenAnheften.ts). Meldet sich dann nicht an die Kopfleiste. */
  sichtbar?: boolean;
  angeheftet?: boolean;
  /** Die frisch gebaute Fassung weicht vom angehefteten Schnappschuss ab. */
  veraltet?: boolean;
  onAnheften?: (an: boolean) => void;
}
export default function BuehnenArbeitsflaeche({ doc, bezug, factory, onModel, sichtbar = true, angeheftet = false, veraltet = false, onAnheften }: AeussereProps) {
  const schnell = bezug?.schnellbuehne;
  const [eingeklappt, setEingeklappt] = useState(false);
  useEffect(() => { setEingeklappt(false); }, [schnell?.id]);
  // The underlying document keeps its normal stage identity and arrangement.
  if (schnell && bezug) bezug = { id: schnell.nachBuehneId, textblock: bezug.textblock, haus: bezug.haus };
  const { user } = useAuth();
  const profil: BuehnenProfil = navigator.maxTouchPoints > 0 || matchMedia('(pointer: coarse)').matches ? 'touch' : 'desktop';
  const prefix = `cui-buehne-v2:${user?.id ?? 'local'}:${profil}`;
  /*
   * SCHMALE FLAECHE = REITER (Rafaels Wahl a, 03.10.2026: „Bühnen am Tablet als Reiter").
   * Touch startete schon mit Tabs; eine schmale Flaeche ohne Touch (geteiltes Fenster,
   * schmaler Browser) startete aber mit der freien Aufteilung und quetschte zwei, drei
   * Fenster auf je ~200 px. Jetzt gilt: ohne ausdrueckliche Wahl entscheidet die Breite
   * der Buehnenflaeche (< SCHMAL_PX) mit. Eine ausdrueckliche Wahl (Knopf oben) und die
   * gespeicherten Anordnungen je Buehne bleiben unangetastet und haben Vorrang.
   * ABGELOEST 14.10.2026 fuer den Desktop (siehe `modusAllgemein`): die Breite entscheidet
   * nur noch, unter welchem Schluessel eine ausdrueckliche Wahl steht, nicht die Vorgabe.
   */
  const wurzel = useRef<HTMLDivElement>(null);
  const [schmal, setSchmal] = useState(() => typeof window !== 'undefined' && window.innerWidth < SCHMAL_PX);
  useEffect(() => {
    const el = wurzel.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => { const w = e.contentRect.width; if (w > 0) setSchmal(w < SCHMAL_PX); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  /*
   * DIE AUSDRUECKLICHE WAHL GILT JE BREITENKLASSE (Nachtrag 03.10.2026). Eine am breiten
   * Bildschirm einmal geklickte „Bühne" stand im selben Schluessel und galt damit auch in
   * jeder schmalen Flaeche — gemessen an der Schmal-Aufnahme aus Rafaels Desktop-Profil:
   * drei Fenster je ~200 px nebeneinander, obwohl die Vorgabe schon „Reiter" war. Schmal
   * hat deshalb einen eigenen Schluessel; die breite Wahl bleibt, wo sie getroffen wurde.
   * Die gespeicherten Anordnungen je Buehne (`anordnungen`) sind davon unberuehrt.
   */
  const modeKey = `${prefix}${schmal ? ':schmal' : ''}:modus`;
  const [lokaleWahl, setLokaleWahl] = useState<Record<string, BuehnenModus>>({});
  const gespeichert = (() => { const saved = lesen<BuehnenModus>(modeKey); return modi.some(([m]) => m === saved) ? saved! : null; })();
  const explizit = lokaleWahl[modeKey] ?? gespeichert;
  /*
   * AM DESKTOP NEBENEINANDER, AUCH WENN DIE FLAECHE SCHMAL IST (Rafael, 14.10.2026, zum
   * wiederholten Mal: „ich weiß auch nicht warum er immer tabs macht statt das gleich sauber
   * links rechts anzuzeigen"). Gemessen: sein Fenster ~1070 CSS-px, unter 1100 px nimmt die
   * Guide-Spalte min(390 px, 45 %), der Buehne bleiben ~680 px < SCHMAL_PX — jede Buehne
   * startete als Reiter, obwohl ihr Rezept zwei Gruppen nebeneinander verlangt
   * (prompts/buehnen-html-technik.md: getrennte Gruppen = nebeneinander). Die Reiter-Vorgabe
   * war fuer das Tablet gewaehlt (03.10.2026) und gilt jetzt wieder nur dort.
   * Eine ausdrueckliche Wahl (Knopf „Ansicht") bleibt unangetastet.
   */
  const modusAllgemein: BuehnenModus = explizit ?? vorgabeModus(profil);
  /*
   * Die Ansicht einer ANGEHEFTETEN Buehne ist eingefroren. Die allgemeine Wahl steht je
   * Geraet in localStorage, nicht je Buehne — wechselt Rafael auf einer anderen Buehne auf
   * „Tabs", wuerde die versteckte beim naechsten Rendern mitwechseln, ihre Ansicht neu
   * aufbauen und jede Webview neu laden. Nur ein Klick auf DIESER Buehne aendert sie.
   */
  const [eigenerModus, setEigenerModus] = useState<BuehnenModus | null>(null);
  useEffect(() => { setEigenerModus(angeheftet ? modusAllgemein : null); }, [angeheftet]); // eslint-disable-line react-hooks/exhaustive-deps
  const modus: BuehnenModus = angeheftet && eigenerModus ? eigenerModus : modusAllgemein;
  const setModus = (m: BuehnenModus) => {
    if (angeheftet) setEigenerModus(m);
    setLokaleWahl(w => ({ ...w, [modeKey]: m }));
    try { merken(modeKey, m); } catch { /* view still switches */ }
  };
  const [remote, setRemote] = useState<Partial<Record<BuehnenModus, GespeicherteAnordnung>> | null>(null);
  const [ladenFehler, setLadenFehler] = useState('');
  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    const stop = new AbortController(); setRemote(null); setLadenFehler('');
    if (!bezug) { setRemote({}); return; }
    void mitNetzWiederholung(async () => {
      const r = await fetch(`/api/guide/buehnen/${encodeURIComponent(bezug.id)}/anordnung?profil=${profil}`, { signal: AbortSignal.any([stop.signal, AbortSignal.timeout(8000)]) }); // trust-boundary:ok response parsed by anordnungenAntwort below
      if (!r.ok) throw new GuideHttpFehler(r.status);
      return anordnungenAntwort.parse(await r.json());
    }, stop.signal, e => {
      setRemote(r => r ?? {});
      setLadenFehler(`${guideNetzFehler(e)}. Änderungen bleiben auf diesem Gerät.`);
    }).then(d => { if (!stop.signal.aborted && d) { setRemote(d.anordnungen ?? {}); setLadenFehler(''); } });
    return () => stop.abort();
  }, [bezug?.id, profil, versuch]);
  const textblock = bezug?.textblock ?? '';
  const hatDokument = !!dokumentTab(doc, textblock);
  const weitereFenster = Math.max(0, alleTabs(doc).length - (hatDokument ? 1 : 0));
  /*
   * DIE WAHL STEHT IM KNOPF „ANSICHT ▾" OBEN (Rafael, 03.10.2026, Ja um 10:00). Die Buehne
   * meldet ihren Stand an die Kopfleiste; nur wo keine Kopfleiste das anbietet (eigene
   * Vorschauseite, Panel allein), stehen die drei Knoepfe wie bisher hier.
   */
  const meldeSchluessel = useRef(Symbol('buehne')).current;
  const setModusRef = useRef(setModus); setModusRef.current = setModus;
  useEffect(() => {
    // Eine versteckte, angeheftete Buehne darf das Kopfmenue nicht an sich ziehen —
    // dort gewinnt die zuletzt gemeldete, und das waere die falsche.
    buehnenAnsichtMelden(meldeSchluessel, sichtbar ? { modus, hatDokument, waehlen: m => setModusRef.current(m) } : null);
  }, [meldeSchluessel, modus, hatDokument, sichtbar]);
  useEffect(() => () => buehnenAnsichtMelden(meldeSchluessel, null), [meldeSchluessel]);
  const wahlOben = useKopfBietetBuehnenWahl();
  const ansichtSchalter = wahlOben ? null : <>
      {modi.map(([m, label]) => <button key={m} type="button" aria-pressed={modus === m} disabled={m === 'dokument' && !hatDokument}
        data-ai-id={`buehnen-modus-${m}`} title={m === 'dokument' ? 'Nur das HTML-Dokument dieser Bühne' : m === 'tabs' ? 'Alle Fenster als Tabs, HTML zuerst' : 'Freie Fensteranordnung'}
        onClick={() => setModus(m)}
        style={{ fontSize: 11, lineHeight: '16px', padding: '2px 7px', minHeight: 24, borderRadius: 4, border: '1px solid var(--tn-border)', cursor: 'pointer', background: modus === m ? 'var(--tn-blue)' : 'var(--tn-bg-dark)', color: modus === m ? '#fff' : 'var(--tn-text)' }}>{label}</button>)}
      {!hatDokument && <span title="Kein eindeutiges HTML-Dokument in dieser Bühne" aria-label="Kein eindeutiges HTML-Dokument in dieser Bühne">ⓘ</span>}
  </>;
  const [anheftFehler, setAnheftFehler] = useState('');
  const anheftKnopf = onAnheften ? <>
    <button type="button" data-ai-id="buehne-anheften" aria-pressed={angeheftet}
      title={angeheftet
        ? 'Angeheftet: bleibt im Hintergrund geöffnet, während du woanders arbeitest. Klick löst sie.'
        : 'Anheften: diese Bühne bleibt im Hintergrund geöffnet (Eingaben bleiben stehen), auch wenn du die Bühne oder den Arbeitsbereich wechselst. Höchstens eine Bühne je Gerät.'}
      onClick={() => { try { setAnheftFehler(''); onAnheften(!angeheftet); } catch (e) { setAnheftFehler(e instanceof Error ? e.message : String(e)); } }}
      style={{ fontSize: 11, lineHeight: '16px', padding: '2px 7px', minHeight: 24, borderRadius: 4, border: '1px solid var(--tn-border)', cursor: 'pointer', background: angeheftet ? 'var(--tn-orange, #e0af68)' : 'var(--tn-bg-dark)', color: angeheftet ? '#1a1b26' : 'var(--tn-text)' }}>
      📌 {angeheftet ? 'Angeheftet' : 'Anheften'}
    </button>
    {veraltet && <span data-ai-id="buehne-angeheftet-veraltet" role="status" style={{ fontSize: 11, color: 'var(--tn-orange, #e0af68)' }}
      title="Die Bühne wurde seit dem Anheften geändert. Angezeigt wird die angeheftete Fassung, damit nichts neu lädt. Lösen und neu öffnen holt die neue.">
      neuere Fassung vorhanden</span>}
    {anheftFehler && <span role="alert" style={{ fontSize: 11, color: 'var(--tn-red)' }}>Anheften fehlgeschlagen: {anheftFehler}</span>}
  </> : null;
  return <div ref={wurzel} style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
    {ladenFehler && <div role="status" style={{ padding: 6, fontSize: 11 }}>{ladenFehler} <button onClick={() => setVersuch(v => v + 1)}>Erneut verbinden</button></div>}
    {/* „Nur das Dokument" verschweigt die uebrigen Fenster nicht mehr (Rafael, 03.10.2026 12:50:
        „am Tablet wird scheinbar nur das Dokument angezeigt, nicht die ganze Bühne"). Ein Tipp
        holt alle Fenster als Reiter, das Dokument bleibt vorne. */}
    {modus === 'dokument' && weitereFenster > 0 && <div data-ai-id="buehne-weitere-fenster" role="status" className="cui-buehne-weitere-fenster">
      <span>Nur das Dokument · {weitereFenster} weitere{weitereFenster === 1 ? 's' : ''} Fenster ausgeblendet</span>
      <button type="button" onClick={() => setModus('tabs')}>Alle Fenster als Reiter zeigen</button>
    </div>}
    <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
      {remote === null && !istFesteAnsicht(modus) ? <div style={{ padding: 12 }}>Anordnung wird geladen …</div> : <Ansicht
        key={`${bezug?.id ?? ''}:${profil}:${modus}:${fingerprint(doc)}:${versuch}`}
        doc={doc} textblock={textblock} modus={modus} profil={profil} bezug={bezug}
        legacyKey={`cui-buehne-anordnung:${fingerprint(doc)}`}
        cacheKey={`${prefix}:${bezug?.id ?? fingerprint(doc)}:${modus}`} remote={remote?.[modus]}
        serverBereit={!ladenFehler} onGespeichert={value => setRemote(r => ({ ...r, [modus]: value }))}
        factory={factory} onModel={onModel} controls={ansichtSchalter} anheftKnopf={anheftKnopf} />}
      {schnell && (eingeklappt ? <button className="schnellbuehne-wieder" onClick={() => setEingeklappt(false)}>Arbeitsbühne öffnen ↗</button> :
        <section className="schnellbuehne-fenster" role="region" aria-label="Schnellbühne als Arbeitsfenster" data-ai-id="schnellbuehne-overlay">
          <header><div><small>ARBEITSBÜHNE · SCHNELLBÜHNE</small><strong>{schnell.titel}</strong></div>
            <button type="button" aria-label="Arbeitsbühne einklappen" onClick={() => setEingeklappt(true)}>−</button>
            <button type="button" aria-label="Zur normalen Bühne" onClick={() => window.dispatchEvent(new Event('cui-schnellbuehne-zurueck'))}>×</button>
          </header>
          <div className="schnellbuehne-fenster-inhalt"><SchnellbuehnePanel html={schnell.html} /></div>
          <footer>Deine normale Bühne bleibt darunter. Änderungen betreffen nur diese Arbeitsbühne.</footer>
        </section>)}
    </div>
  </div>;
}

/*
 * KNOEPFE IM REITER EINER EIGENEN CUI-SEITE (Rafael, 03.10.2026: Bühnenfenster
 * entschlacken — „Ja"). Neu laden und im neuen Tab oeffnen stehen bei einer
 * eigenen CUI-Seite zusaetzlich als zwei kleine Zeichen im Reiter. Die
 * Adresszeile selbst ist inzwischen in jeder Buehnen-Browserkachel immer sichtbar
 * und hat hier deshalb keinen Schalter mehr. Fremde App-Adressen behalten ihre
 * Adresszeile und bekommen hier nichts. Die Knoepfe sprechen das Panel ueber dieselben
 * Fenster-Ereignisse an wie die Fernsteuerung (`browser-reload` u. a.).
 */
const reiterKnopfStil: CSSProperties = { border: 'none', background: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', padding: '0 3px', fontSize: 12, lineHeight: '16px' };
function reiterKnoepfe(node: TabNode, rv: ITabRenderValues) {
  if (node.getComponent() !== 'browser' || !istEigeneCuiSeite(node.getConfig()?.url)) return;
  const panelId = node.getId();
  const senden = (name: string, detail: Record<string, unknown> = {}) => (e: MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    window.dispatchEvent(new CustomEvent(name, { detail: { panelId, ...detail } }));
  };
  // Ohne das stoppt flexlayout den Klick nicht: der Reiter wuerde ihn als Ziehen deuten.
  const halt = (e: SyntheticEvent) => e.stopPropagation();
  rv.buttons.push(
    <span key="cui-reiter-knoepfe" style={{ display: 'inline-flex', alignItems: 'center', gap: 1, marginLeft: 4 }} onPointerDown={halt} onMouseDown={halt}>
      <button type="button" data-ai-id="buehnen-reiter-neu-laden" title="Seite neu laden" aria-label="Seite neu laden" style={reiterKnopfStil} onClick={senden('browser-reload')}>&#8635;</button>
      <button type="button" data-ai-id="buehnen-reiter-neuer-tab" title="In neuem Tab öffnen" aria-label="In neuem Tab öffnen" style={reiterKnopfStil} onClick={senden(OEFFNEN_EREIGNIS)}>&#8599;</button>
    </span>,
  );
}

interface AnsichtProps extends Props {
  controls: ReactNode;
  anheftKnopf: ReactNode;
  textblock: string; modus: BuehnenModus; profil: BuehnenProfil; cacheKey: string; legacyKey: string;
  remote?: GespeicherteAnordnung; serverBereit: boolean;
  onGespeichert: (value: GespeicherteAnordnung) => void;
}
function Ansicht({ doc, textblock, modus, profil, bezug, cacheKey, legacyKey, remote, serverBereit, onGespeichert, factory, onModel, controls, anheftKnopf }: AnsichtProps) {
  const browserModel = (value: IJsonModel) => Model.fromJson(browserStandard(value, { haus: bezug?.haus, origin: window.location.origin }));
  const basis = standardAnsicht(doc, modus, textblock);
  // Dokument und Reiter sind feste Ansichten (shared/buehnen-anordnung.ts, istFesteAnsicht):
  // sie lesen keine gespeicherte Anordnung und schreiben keine — weder lokal noch auf den Server.
  const fest = istFesteAnsicht(modus);
  const initialCache = useRef((() => {
    if (fest) return null;
    const saved = lesen<{ anordnung: Anordnung; pending: boolean; version: number }>(cacheKey);
    if (saved || remote || modus !== 'buehne') return saved;
    const legacy = lesen<IJsonModel>(legacyKey);
    return legacy ? { anordnung: { basis: basisTabs(basis, textblock), layout: legacy }, pending: true, version: 0 } : null;
  })());
  const [model, setModel] = useState(() => {
    const local = initialCache.current;
    if (fest) return browserModel(basis);
    const saved = local?.pending || !remote || (local && local.version > remote.version) ? local?.anordnung : remote.anordnung;
    try { return browserModel(wiederherstellen(basis, textblock, saved, { haus: bezug?.haus, origin: window.location.origin })); } catch { return browserModel(basis); }
  });
  const [status, setStatus] = useState('');
  const [, render] = useState(0);
  const [saving, setSaving] = useState(false);
  const version = useRef(Math.max(remote?.version ?? 0, initialCache.current?.version ?? 0));
  const pending = useRef<Anordnung | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const konflikt = useRef(false);
  const lokalGeaendert = useRef(false);
  const alive = useRef(true);
  const onGespeichertRef = useRef(onGespeichert); onGespeichertRef.current = onGespeichert;
  useEffect(() => { onModel(model); return () => onModel(null); }, [model, onModel]);
  const speichern = useCallback(() => {
    const value = pending.current; pending.current = null;
    if (!value || !bezug || !serverBereit || konflikt.current) return;
    const job = (speicherQueues.get(cacheKey) ?? Promise.resolve()).then(async () => {
      if (konflikt.current) return;
      version.current = Math.max(version.current, lesen<{ version: number }>(cacheKey)?.version ?? 0);
      if (alive.current) setSaving(true);
      try {
        const r = await fetch(`/api/guide/buehnen/${encodeURIComponent(bezug.id)}/anordnung`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, keepalive: true,
          body: JSON.stringify({ profil, modus, version: version.current, anordnung: value }),
        });
        if (r.status === 409) { konflikt.current = true; throw Error('Die Anordnung wurde in einem anderen Fenster geändert. Bitte neu laden, bevor du weiter speicherst.'); }
        if (!r.ok) throw Error(`Speichern auf dem Server fehlgeschlagen (HTTP ${r.status}). Lokal bleibt die Anordnung erhalten.`);
        const saved = await r.json() as GespeicherteAnordnung;
        version.current = saved.version;
        const local = lesen<{ anordnung: Anordnung }>(cacheKey);
        // A slow response must not replace a more recent unsent drag/close.
        if (local) merken(cacheKey, { anordnung: local.anordnung, pending: JSON.stringify(local.anordnung) !== JSON.stringify(value), version: saved.version });
        if (alive.current) { setStatus('Gespeichert'); onGespeichertRef.current(saved); }
      } catch (e) { if (alive.current) setStatus(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.'); }
      finally { if (alive.current) setSaving(false); }
    });
    speicherQueues.set(cacheKey, job);
    void job.finally(() => { if (speicherQueues.get(cacheKey) === job) speicherQueues.delete(cacheKey); });
  }, [bezug?.id, profil, modus, serverBereit, cacheKey]);
  const speichernRef = useRef(speichern); speichernRef.current = speichern;
  useEffect(() => {
    alive.current = true;
    const flush = () => { if (timer.current) clearTimeout(timer.current); speichernRef.current(); };
    window.addEventListener('pagehide', flush);
    return () => { alive.current = false; window.removeEventListener('pagehide', flush); flush(); };
  }, []);
  useEffect(() => {
    if (!serverBereit) return;
    const local = lesen<{ anordnung: Anordnung; pending: boolean; version: number }>(cacheKey);
    if (local?.pending) {
      if (remote && local.version !== remote.version) {
        konflikt.current = true; setStatus('Lokale Änderungen und Serverstand unterscheiden sich. Wähle „Serverstand laden“ oder behalte deine lokale Anordnung.');
        return;
      }
      pending.current = local.anordnung; speichern();
      return;
    }
    // A recovered GET may restore the server layout only before any local edit.
    if (remote && !lokalGeaendert.current && !fest) {
      version.current = remote.version;
      setModel(browserModel(wiederherstellen(basis, textblock, remote.anordnung, { haus: bezug?.haus, origin: window.location.origin })));
    }
  // basis is derived on render; depend on the received revision, not its object identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverBereit, remote?.version, speichern]);
  const merkenModel = (m: Model) => {
    if (fest) { onModel(m); render(n => n + 1); return; }
    lokalGeaendert.current = true;
    const value: Anordnung = { basis: basisTabs(basis, textblock), layout: m.toJson() };
    pending.current = value;
    onModel(m);
    try { merken(cacheKey, { anordnung: value, pending: true, version: version.current }); setStatus(bezug && serverBereit ? 'Wird gespeichert …' : 'Auf diesem Gerät gespeichert'); }
    catch { setStatus('Lokaler Speicher nicht verfügbar.'); }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(speichern, 500); render(n => n + 1);
  };
  const offen = new Set(alleTabs(model.toJson()).map(t => t.id));
  const geschlossen = alleTabs(basis).filter(t => !offen.has(t.id));
  return <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
    <div className="cui-stage-toolbar" role="group" aria-label="Bühnenansicht" data-ai-id="buehnen-ansicht">
      {(controls || anheftKnopf) && <div className="cui-stage-modes">{controls}{anheftKnopf}</div>}
      {!fest && <details className="cui-stage-options" onKeyDown={e => { if (e.key === 'Escape') { e.currentTarget.open = false; e.currentTarget.querySelector('summary')?.focus(); } }}>
        <summary title="Anordnung und geschlossene Fenster" aria-label="Bühnenoptionen">⋯</summary>
        <div className="cui-stage-options-menu">
      <button style={controlStyle} data-ai-id="buehnen-anordnung-reset" onClick={() => { const m = browserModel(basis); setModel(m); merkenModel(m); }}>Anordnung zurücksetzen</button>
      {!!geschlossen.length && <select style={controlStyle} aria-label="Geschlossenes Fenster öffnen" value="" onChange={e => {
        const tab = geschlossen.find(t => t.id === e.target.value); if (!tab) return;
        let ziel = model.getActiveTabset();
        if (!ziel) model.visitNodes(n => { if (!ziel && n.getType() === 'tabset') ziel = n as any; });
        if (ziel) { model.doAction(Actions.addNode(browserStandardTab(tab, { haus: bezug?.haus, origin: window.location.origin }) as any, ziel.getId(), DockLocation.CENTER, -1, true)); merkenModel(model); }
        else {
          const json = model.toJson(); json.layout.children.push({ type: 'tabset', children: [tab as any] });
          const restored = browserModel(json); setModel(restored); merkenModel(restored);
        }
      }}><option value="">Geschlossene Fenster ({geschlossen.length})</option>{geschlossen.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}

      {konflikt.current && <>
        <button style={controlStyle} onClick={() => { localStorage.removeItem(cacheKey); window.location.reload(); }}>Serverstand laden</button>
        <button style={controlStyle} onClick={async () => {
          if (!bezug) return;
          try {
            const r = await fetch(`/api/guide/buehnen/${encodeURIComponent(bezug.id)}/anordnung?profil=${profil}`);
            if (!r.ok) throw Error('Serverstand konnte nicht gelesen werden.');
            const latest = await r.json(); version.current = latest.anordnungen?.[modus]?.version ?? 0;
            konflikt.current = false; merkenModel(model);
          } catch (e) { setStatus(String(e)); }
        }}>Meine Anordnung übernehmen</button>
      </>}
        </div>
      </details>}
      <span className="cui-stage-status" role="status" data-ai-id="buehnen-speicherstatus" title={status}>{saving ? 'Wird gespeichert …' : status}</span>
    </div>
    <div style={{ flex: 1, minHeight: 0, position: 'relative' }} data-ai-id="buehnen-fenster">
      <BuehnenKontext.Provider value={true}>
      <BuehnenKennungKontext.Provider value={bezug?.id ?? null}>
        <Layout model={model} factory={factory} onModelChange={merkenModel} onRenderTab={reiterKnoepfe} />
      </BuehnenKennungKontext.Provider>
      </BuehnenKontext.Provider>
    </div>
  </div>;
}
